/**
 * PF Revamp — Place Order mutation hook (Module 13 task 5).
 *
 * Slice B1 (data shift, 2026-06-23): the body is now the REAL v2 wire DTO
 * (`PlaceOrderClientV2DTO` / `PlaceOrderAdminV2DTO`) built by
 * `serializePlacement(state, catalogue)` — productCode-keyed product lines,
 * `billingMethod`, `organizationMethod`, real `sessionId`. The old
 * `{ sessionId, draft: OrderDraftDTO }` shape is gone. See `serializePlacement.ts`
 * and `models/purchaseFlowRevamp/placementV2DTO.ts` for the seams (configuration,
 * dependentProducts, inputImages, fastDelivery, admin email).
 *
 * Security fix — KB H7: the legacy `PlaceOrderBody.role: Auth0Roles` is
 * REMOVED from the revamp payload. The backend derives the role from
 * the JWT. Trusting a client-supplied role is an authorization bypass.
 *
 * Payload composition:
 *   - `serializePlacement(state, catalogue)` resolves the flat cart into
 *     productCode-keyed lines against the resolved catalogue.
 *   - Admin scope (`targetUserId` / `targetWorkspaceId`) selects the admin
 *     body + routes the POST to `/workspace/{id}/orders`; client scope POSTs
 *     to the client path.
 *   - Payment branching happens at the caller (Summary page):
 *       * `card` | `sepa` → create Stripe intent, redirect to Stripe
 *         checkout via `return_url` handoff to `PaymentStatus.page`.
 *       * `invoice` → POST order directly, navigate to confirmation.
 *
 * The hook returns:
 *   - `placeOrder(options)`  — always POSTs the order; returns the
 *     server response (`{ id: number }`).
 *   - `createPaymentIntent`  — wraps `useCreateUpdatePFIntent` so the
 *     Summary page can kick off the Stripe redirect before POSTing
 *     the order (mirrors legacy flow).
 *   - `isPending`, `error`    — standard mutation state.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ASYMMETRY — Invoice REAL vs Stripe legacy (B1-finalize, 2026-06-25):
 *
 * ┌─────────────────┬─────────────────────────────────────────────────────┐
 * │ Payment method  │ Current state                                       │
 * ├─────────────────┼─────────────────────────────────────────────────────┤
 * │ invoice         │ REAL. `mutationFn` POSTs the v2 wire body to         │
 * │                 │ `POST /workspace/{ws}/orders` (placeOrderInWorkspace │
 * │                 │ V2) with the v2 media type and returns the BE order  │
 * │                 │ id (`IdentityDTOLong { id }`). The synthetic mock is │
 * │                 │ retained ONLY behind VITE_PLACE_ORDER_MOCK=true      │
 * │                 │ (demo / storybook) — see the escape hatch below.     │
 * ├─────────────────┼─────────────────────────────────────────────────────┤
 * │ card / sepa     │ PLACEMENT is wired (this hook can POST), but the     │
 * │                 │ Stripe *intent* amount path (`useRevampPaymentIntent`)│
 * │                 │ is not yet v2-live, so the Summary page still routes  │
 * │                 │ card/sepa through the legacy `PlaceOrderHandler-      │
 * │                 │ Publisher` → `legacyOnPlaceOrder()` (in              │
 * │                 │ LegacyStepShell.tsx). Flipping card/sepa onto this   │
 * │                 │ hook is a follow-up once the v2 payment-intent lands. │
 * └─────────────────┴─────────────────────────────────────────────────────┘
 *
 * The POST is LIVE by default. The request DTO matches the deployed contract
 * (`PlaceOrderClientV2DTO` / `PlaceOrderAdminV2DTO`); the response is
 * `IdentityDTOLong { id: number }`, mapped straight onto
 * `RevampPlaceOrderResponse.id` (the Stripe return-url reconcile depends on it).
 * The workspace id is the session's own (`usePfSessionWorkspaceId`): the order is
 * placed from the session, and the backend refuses a session addressed elsewhere.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { AxiosResponse } from 'axios'

import { logger } from 'utils/logging'
import { EntityKeys } from 'utils/reactQuery'
import { useAPIV2 } from 'utils/API'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTimezone } from 'components/contexts/timezone.context'

import type { PlaceOrderV2DTO } from 'models/purchaseFlowRevamp'
import { useCreateUpdatePFIntent } from 'dataQueries/purchase.query'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { serializePlacement } from 'components/pages/PurchaseFlowRevamp/store/serializePlacement'
import { useBillingPaymentMethods } from 'components/pages/PurchaseFlowRevamp/steps/billing/useBillingPaymentMethods'
import { useDefaultInvoicingAddress } from 'components/pages/PurchaseFlowRevamp/steps/billing/useDefaultInvoicingAddress'
import { resolveDefaultCcEmails, resolveDefaultReference } from 'components/pages/PurchaseFlowRevamp/steps/billing/billingDefaults'
import { useUserData } from 'components/contexts/UserDataContext'
import { selectCoveredByQuota } from 'components/pages/PurchaseFlowRevamp/steps/billing/chargeable'
import { selectUnresolvedCartItemIds } from 'components/pages/PurchaseFlowRevamp/store/selectors'
import { useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'
import { usePfOrderAttemptId, usePfOrderLaunchedFrom } from 'components/pages/PurchaseFlowRevamp/routing/pfOrderAttemptId'

enum Endpoints {
  /** POST — place a v2 order (placeOrderInWorkspaceV2). */
  PLACE_ORDER_V2 = '/workspace/{workspaceId}/orders',
}

/** Synthetic delay so the place-order spinner state is visible when the
 *  VITE_PLACE_ORDER_MOCK escape hatch is active (demo / storybook). */
const MOCK_RESPONSE_DELAY_MS = 600

/** Escape hatch: keep the synthetic mock available for demo / storybook builds. */
const isPlaceOrderMockForced = (): boolean =>
  import.meta.env.VITE_PLACE_ORDER_MOCK === 'true'

export interface RevampPlaceOrderResponse {
  /** BE-issued order id (`IdentityDTOLong.id` from `placeOrderInWorkspaceV2`).
   *  Used as the `dealId` query param for the Stripe return URL so
   *  `PaymentStatus.page` can reconcile. */
  id: number
}

/** Shape of the useRevampPlaceOrder return. */
export interface UseRevampPlaceOrderResult {
  placeOrder: (overrides?: Partial<PlaceOrderV2DTO>) => Promise<AxiosResponse<RevampPlaceOrderResponse>>
  /** Build the v2 wire body from current store state + resolved catalogue.
   *  Returns null when a hard precondition (sessionId / invoicing address) is
   *  missing. NOT paymentMethod — a free order legitimately carries none since
   *  ISSUE-3107. */
  buildBody: () => PlaceOrderV2DTO | null
  isPending: boolean
  error: unknown
  reset: () => void
  /** Sibling helper: create / update the Stripe payment intent. Caller
   *  uses the returned `clientSecret` to launch Stripe's redirect UX. */
  createPaymentIntent: ReturnType<typeof useCreateUpdatePFIntent>
}

/**
 * Place Order mutation hook.
 *
 * Body assembly (Slice B1):
 *   - `serializePlacement(state, catalogue)` builds the v2 wire DTO:
 *     productCode-keyed product lines (addons from options + extra services),
 *     `billingMethod`, `organizationMethod`, `sessionId`, invoicing/reference.
 *   - The resolved catalogue (from `useCatalogue`, cached by the time the user
 *     reaches Summary) supplies cart-id → productCode resolution.
 *   - Admin scope selects `PlaceOrderAdminV2DTO`; the POST path is routed by
 *     `state.targetWorkspaceId`.
 *
 * NOTE: `role` is NOT included. BE derives from JWT. KB H7.
 */
export function useRevampPlaceOrder(): UseRevampPlaceOrderResult {
  const createPaymentIntent = useCreateUpdatePFIntent()
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { spawnWarningToast } = useSnackbar()
  const api = useAPIV2<Endpoints>()
  // Resolved user tz (user.defaultTimezone → browser → Europe/Zurich) for the
  // org-method `timezone` wire-fill on modes the FE forms don't collect it for.
  const { userTimezone } = useTimezone()

  // R-3 CLOSED (ledger #14): the actual wire kind behind the org's `'card'`
  // option (`CARD_PAYMENT` | `STRIPE_PAYMENT`), so `serializePlacement` can
  // echo it back instead of assuming Stripe. Same shared instruction-options
  // cache entry Billing/useStepNav already mount — costs nothing extra here.
  const { cardPaymentKind, defaultPaymentMethod } = useBillingPaymentMethods()
  // The SHOOT's zone, which is the one the meeting time was
  // entered in: the pickers are bound to it and the step warns when it differs
  // from the client's. Without it `serializePlacement` falls back to the client's
  // zone and every on-site order ships labelled with the wrong one. Same shared
  // instruction-options cache entry the line above already mounts, so it is free.
  const { data: instructionOptions, isRefreshing: isInstructionOptionsRefreshing } = useInstructionOptions()
  const shootTimezone = instructionOptions?.timezone ?? undefined
  // The order must be built on the same site-access verdict the step was gated
  // on; deriving it twice is how the body and the screen come to disagree.
  const instructionsVisible = instructionOptions?.visible
  // The same default the Billing gate opened on has to reach the
  // body, or Proceed works and assembly throws instead.
  const defaultInvoicingAddress = useDefaultInvoicingAddress()
  // The CC list Billing shows before the client edits it; without it the prefilled recipients never reach the order.
  const { clientData, currentWorkspaceCcEmails } = useUserData()
  const defaultCcEmails = useMemo(
    () => resolveDefaultCcEmails(clientData?.ccEmails, currentWorkspaceCcEmails),
    [clientData?.ccEmails, currentWorkspaceCcEmails]
  )

  const { data: catalogue } = usePfCatalogue()
  const orderAttemptId = usePfOrderAttemptId()
  const launchedFrom = usePfOrderLaunchedFrom()
  const queryClient = useQueryClient()

  // Workspace the order is placed in — same resolution as useCatalogue /
  // useEnsurePfV2Session: admin → picked target; client → its property workspace.
  const workspaceId = usePfSessionWorkspaceId()

  const buildBody = useCallback((): PlaceOrderV2DTO | null => {
    const state = usePfStore.getState()

    if (!state.draft.sessionId) {
      logger.warn('pf-revamp place order blocked: sessionId missing', undefined, {
        context: { component: 'useRevampPlaceOrder' },
      })
      return null
    }
    // Symmetric with the serializer's own guard. The Billing gate
    // requires a saved address for every payment method, so reaching here
    // without one means a gate was relaxed, not that the client skipped a step:
    // warn rather than fail silently. An empty id is an address the client
    // started and never saved (the legacy `'new'` sentinel).
    if (!state.invoicingAddress?.id && !defaultInvoicingAddress?.id) {
      logger.warn('pf-revamp place order blocked: no saved invoicing address', undefined, {
        context: { component: 'useRevampPlaceOrder' },
      })
      return null
    }
    // Judge the EFFECTIVE method, the same one the step gates use. A
    // client who accepts the BE-preferred preselect never writes the slice
    // (§11.1 bans mount-time seeding), so demanding the raw value here opened
    // the gates and then refused to build the body.
    //
    // No longer fatal when BOTH are absent: since ISSUE-3107 an order
    // with nothing to pay for carries no billing method. The serializer omits
    // the field; the BE rejects the order if it disagrees about chargeability,
    // which is the right place for that judgement to be made.
    if (!state.paymentMethod && !defaultPaymentMethod) {
      logger.info('pf-revamp place order: no payment method — sending none (free order path)', undefined, {
        context: { component: 'useRevampPlaceOrder' },
      })
    }

    // The options still belong to the cart before the last edit: its site-access verdict and timezone
    // would decide this order's meeting.
    if (isInstructionOptionsRefreshing) {
      logger.warn('pf-revamp place order blocked: instruction options still refreshing', undefined, {
        context: { component: 'useRevampPlaceOrder' },
      })
      return null
    }

    // Without the catalogue no line resolves, and the order would go out with no products.
    if (!catalogue) {
      logger.warn('pf-revamp place order blocked: catalogue not loaded', undefined, {
        context: { component: 'useRevampPlaceOrder', metadata: { cartItemCount: state.items.length } },
      })
      return null
    }

    const unresolvedLines = selectUnresolvedCartItemIds(catalogue)(state).length
    if (unresolvedLines > 0) {
      logger.warn('pf-revamp place order blocked: cart lines missing from the catalogue', undefined, {
        context: { component: 'useRevampPlaceOrder', metadata: { cartItemCount: state.items.length, unresolvedLines } },
      })
      return null
    }

    return serializePlacement(state, catalogue, {
      userTimezone, locationTimezone: shootTimezone, cardPaymentKind, defaultPaymentMethod, defaultInvoicingAddress,
      defaultReference: resolveDefaultReference(state.property), defaultCcEmails,
      instructionsVisible, nothingToCharge: selectCoveredByQuota(state), orderAttemptId, launchedFrom,
    })
  }, [catalogue, isInstructionOptionsRefreshing, userTimezone, shootTimezone, instructionsVisible, cardPaymentKind, defaultPaymentMethod, defaultInvoicingAddress, defaultCcEmails, orderAttemptId, launchedFrom])

  const mutation = useMutation<AxiosResponse<RevampPlaceOrderResponse>, unknown, PlaceOrderV2DTO>({
    mutationFn: async (body) => {
      // ── Escape hatch: synthetic mock for demo / storybook builds ──
      // VITE_PLACE_ORDER_MOCK=true → no BE call, synthetic id. The default
      // (flag unset) is the REAL POST below.
      if (isPlaceOrderMockForced()) {
        logger.info('pf-revamp place-order (MOCK — VITE_PLACE_ORDER_MOCK=true)', undefined, {
          context: {
            component: 'useRevampPlaceOrder',
            action: 'placeOrderMock',
            metadata: {
              sessionId: body.sessionId,
              paymentMethod: body.billingMethod?.paymentMethod ?? null,
              isAdmin: 'email' in body,
              workspaceId: workspaceId || null,
              productCount: body.products.length,
              fastDelivery: body.fastDelivery,
            },
          },
        })

        // Warn the user visually so nobody mistakes this for a real order.
        spawnWarningToast(t('purchase_flow_revamp:summary.mock_order_warning'), { timeout: 8000 })

        await new Promise((resolve) => setTimeout(resolve, MOCK_RESPONSE_DELAY_MS))

        const syntheticId = Math.floor(Math.random() * 90_000_000) + 10_000_000
        return {
          data: { id: syntheticId },
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as never,
        } as AxiosResponse<RevampPlaceOrderResponse>
      }

      // ── REAL POST /workspace/{workspaceId}/orders (placeOrderInWorkspaceV2) ──
      // keepResponse=true → returns the AxiosResponse, and
      // `IdentityDTOLong { id }` maps straight onto RevampPlaceOrderResponse.
      if (!workspaceId) {
        throw new Error('useRevampPlaceOrder: workspace id unresolved — cannot place order')
      }
      return api.post<RevampPlaceOrderResponse>(Endpoints.PLACE_ORDER_V2, { workspaceId }, body, true)
    },
    // The next order click reads the property re-order context from the cache
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EntityKeys.PROPERTY_REORDER_CONTEXT] }),
  })

  const placeOrder = useCallback(
    async (overrides?: Partial<PlaceOrderV2DTO>) => {
      const base = buildBody()
      if (!base) {
        throw new Error('place-order body could not be assembled from current state')
      }
      const body = { ...base, ...overrides } as PlaceOrderV2DTO
      // Runtime guard: make absolutely sure no `role` field leaks in.
      // (Defensive against future callers passing `overrides` that
      // include it by mistake.) KB H7.
      if ('role' in (body as unknown as Record<string, unknown>)) {
        delete (body as unknown as Record<string, unknown>).role
      }
      return mutation.mutateAsync(body)
    },
    [buildBody, mutation]
  )

  return useMemo(
    () => ({
      placeOrder,
      buildBody,
      isPending: mutation.isPending,
      error: mutation.error,
      reset: mutation.reset,
      createPaymentIntent,
    }),
    [buildBody, createPaymentIntent, mutation.error, mutation.isPending, mutation.reset, placeOrder]
  )
}
